import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PWAService } from './pwa.service';

export interface NotificationData {
  id: string;
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  timestamp: number;
  read: boolean;
  actions?: NotificationAction[];
  category: 'info' | 'warning' | 'success' | 'error';
  priority: 'low' | 'normal' | 'high';
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private pwaService = inject(PWAService);
  private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  private permissionGranted = false;
  private registration: ServiceWorkerRegistration | null = null;
  private userInteracted = false;

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = new BehaviorSubject<number>(0);

  constructor() {
    this.initializeNotifications();
    this.setupMockNotifications();
    this.setupUserInteractionTracking();
  }

  private async initializeNotifications(): Promise<void> {
    // Request notification permission
    this.permissionGranted =
      await this.pwaService.requestNotificationPermission();

    // Get service worker registration
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.ready;
        this.setupPushSubscription();
      } catch (error) {
        console.error('Service Worker not available:', error);
      }
    }

    // Load stored notifications
    this.loadStoredNotifications();
  }

  private async setupPushSubscription(): Promise<void> {
    if (!this.registration || !this.permissionGranted) return;

    try {
      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();

      if (!subscription) {
        // Create new subscription
        const publicKey = this.urlBase64ToUint8Array(
          'your-vapid-public-key-here'
        );
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: publicKey,
        });
      }

      console.log('Push subscription created:', subscription);
      // Here you would send the subscription to your server
    } catch (error) {
      console.error('Error setting up push subscription:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private setupMockNotifications(): void {
    // Simulate real-time notifications for demo
    setInterval(() => {
      if (Math.random() < 0.1) {
        // 10% chance every interval
        this.generateMockNotification();
      }
    }, 30000); // Check every 30 seconds

    // Initial mock notifications
    setTimeout(() => {
      this.addMockInitialNotifications();
    }, 5000);
  }

  private addMockInitialNotifications(): void {
    const initialNotifications: Partial<NotificationData>[] = [
      {
        title: '📦 Estoque baixo',
        body: '5 produtos estão com estoque crítico',
        category: 'warning',
        priority: 'high',
        data: { type: 'stock-alert', products: ['Produto A', 'Produto B'] },
      },
      {
        title: '💰 Nova venda registrada',
        body: 'Venda de R$ 250,00 - João Silva',
        category: 'success',
        priority: 'normal',
        data: { type: 'sale', amount: 250, customer: 'João Silva' },
      },
      {
        title: '🚀 Bem-vindo ao Estokai PWA!',
        body: 'Explore todas as funcionalidades offline',
        category: 'info',
        priority: 'normal',
        data: { type: 'welcome' },
      },
    ];

    initialNotifications.forEach((notif, index) => {
      setTimeout(() => {
        this.addNotification(notif);
      }, index * 2000);
    });
  }

  private generateMockNotification(): void {
    const mockNotifications = [
      {
        title: '🛒 Novo pedido',
        body: `Pedido #${Math.floor(Math.random() * 1000)} recebido`,
        category: 'info' as const,
        priority: 'normal' as const,
        data: { type: 'order', orderId: Math.floor(Math.random() * 1000) },
      },
      {
        title: '📊 Relatório pronto',
        body: 'Relatório mensal de vendas disponível',
        category: 'success' as const,
        priority: 'normal' as const,
        data: { type: 'report', reportType: 'monthly-sales' },
      },
      {
        title: '⚠️ Produto vencendo',
        body: 'Produto X vence em 3 dias',
        category: 'warning' as const,
        priority: 'high' as const,
        data: { type: 'expiry', product: 'Produto X', days: 3 },
      },
      {
        title: '🎯 Meta atingida!',
        body: 'Parabéns! Meta mensal de vendas atingida',
        category: 'success' as const,
        priority: 'high' as const,
        data: { type: 'goal', goalType: 'monthly-sales' },
      },
    ];

    const notification =
      mockNotifications[Math.floor(Math.random() * mockNotifications.length)];
    this.addNotification(notification);
  }

  public addNotification(notificationData: Partial<NotificationData>): void {
    const notification: NotificationData = {
      id: this.generateId(),
      timestamp: Date.now(),
      read: false,
      icon: '/assets/icons/icon-192x192.png',
      badge: '/assets/icons/icon-72x72.png',
      ...notificationData,
      title: notificationData.title || 'Notificação',
      body: notificationData.body || '',
      category: notificationData.category || 'info',
      priority: notificationData.priority || 'normal',
    };

    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];

    // Keep only last 50 notifications
    const limitedNotifications = updatedNotifications.slice(0, 50);

    this.notificationsSubject.next(limitedNotifications);
    this.updateUnreadCount();
    this.storeNotifications(limitedNotifications);

    // Show browser notification if permission granted
    if (this.permissionGranted) {
      this.showBrowserNotification(notification);
    }
  }

  private async showBrowserNotification(
    notification: NotificationData
  ): Promise<void> {
    try {
      // Handle vibration using safe method
      const vibrationPattern =
        notification.priority === 'high' ? [200, 100, 200] : [100];
      this.safeVibrate(vibrationPattern);

      // Provide fallback feedback if vibration is not available
      this.provideFallbackFeedback(notification.priority);

      await this.pwaService.showNotification(notification.title, {
        body: notification.body,
        icon: notification.icon,
        badge: notification.badge,
        data: notification.data,
        tag: notification.id,
        // Note: actions are handled by service worker, not directly in options
      });
    } catch (error) {
      console.error('Error showing browser notification:', error);
    }
  }

  public markAsRead(notificationId: string): void {
    const notifications = this.notificationsSubject.value.map((notif) =>
      notif.id === notificationId ? { ...notif, read: true } : notif
    );

    this.notificationsSubject.next(notifications);
    this.updateUnreadCount();
    this.storeNotifications(notifications);
  }

  public markAllAsRead(): void {
    const notifications = this.notificationsSubject.value.map((notif) => ({
      ...notif,
      read: true,
    }));

    this.notificationsSubject.next(notifications);
    this.updateUnreadCount();
    this.storeNotifications(notifications);
  }

  public deleteNotification(notificationId: string): void {
    const notifications = this.notificationsSubject.value.filter(
      (notif) => notif.id !== notificationId
    );

    this.notificationsSubject.next(notifications);
    this.updateUnreadCount();
    this.storeNotifications(notifications);
  }

  public clearAllNotifications(): void {
    this.notificationsSubject.next([]);
    this.unreadCount$.next(0);
    this.storeNotifications([]);
  }

  private updateUnreadCount(): void {
    const unreadCount = this.notificationsSubject.value.filter(
      (notif) => !notif.read
    ).length;
    this.unreadCount$.next(unreadCount);
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private storeNotifications(notifications: NotificationData[]): void {
    try {
      localStorage.setItem(
        'estokai-notifications',
        JSON.stringify(notifications)
      );
    } catch (error) {
      console.error('Error storing notifications:', error);
    }
  }

  private loadStoredNotifications(): void {
    try {
      const stored = localStorage.getItem('estokai-notifications');
      if (stored) {
        const notifications: NotificationData[] = JSON.parse(stored);
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount();
      }
    } catch (error) {
      console.error('Error loading stored notifications:', error);
    }
  }

  /**
   * Configura o rastreamento de interação do usuário
   */
  private setupUserInteractionTracking(): void {
    const interactionEvents = ['click', 'touchstart', 'keydown', 'scroll'];

    const handleInteraction = () => {
      this.userInteracted = true;
      // Remove listeners após primeira interação
      interactionEvents.forEach((event) => {
        document.removeEventListener(event, handleInteraction, true);
      });
    };

    // Adiciona listeners para detectar primeira interação
    // Usando capture=true para garantir que o evento seja capturado
    interactionEvents.forEach((event) => {
      try {
        document.addEventListener(event, handleInteraction, true);
      } catch (error) {
        // Fallback para navegadores mais antigos
        document.addEventListener(event, handleInteraction);
      }
    });
  }

  // Demo methods for testing
  public sendTestNotification(): void {
    this.addNotification({
      title: '🧪 Notificação de teste',
      body: 'Esta é uma notificação de teste do Estokai PWA',
      category: 'info',
      priority: 'normal',
      data: { type: 'test' },
    });
  }

  public sendUrgentNotification(): void {
    this.addNotification({
      title: '🚨 Alerta urgente!',
      body: 'Sistema detectou atividade suspeita',
      category: 'error',
      priority: 'high',
      data: { type: 'security-alert' },
      actions: [
        { action: 'view', title: 'Ver detalhes' },
        { action: 'dismiss', title: 'Dispensar' },
      ],
    });
  }

  /**
   * Show a notification (public method for external use)
   */
  public showNotification(notification: Partial<NotificationData>): void {
    const notificationData: NotificationData = {
      id: notification.id || this.generateId(),
      title: notification.title || 'Notification',
      body: notification.body || '',
      icon: notification.icon || '/assets/icons/icon-192x192.png',
      timestamp: notification.timestamp || Date.now(),
      read: false,
      category: notification.category || 'info',
      priority: notification.priority || 'normal',
      actions: notification.actions || [],
      ...notification,
    };

    this.addNotification(notificationData);
  }

  /**
   * Show success notification
   */
  public showSuccess(message: string, title?: string): void {
    this.showNotification({
      title: title || '✅ Sucesso',
      body: message,
      category: 'success',
      priority: 'normal',
    });
  }

  /**
   * Show error notification
   */
  public showError(message: string, title?: string): void {
    this.showNotification({
      title: title || '❌ Erro',
      body: message,
      category: 'error',
      priority: 'high',
    });
  }

  /**
   * Show info notification
   */
  public showInfo(message: string, title?: string): void {
    this.showNotification({
      title: title || 'ℹ️ Informação',
      body: message,
      category: 'info',
      priority: 'normal',
    });
  }

  /**
   * Show warning notification
   */
  public showWarning(message: string, title?: string): void {
    this.showNotification({
      title: title || '⚠️ Aviso',
      body: message,
      category: 'warning',
      priority: 'normal',
    });
  }

  /**
   * Verifica se a vibração pode ser usada de forma segura
   */
  private canUseVibration(): boolean {
    return 'vibrate' in navigator && this.userInteracted;
  }

  /**
   * Executa vibração de forma segura, apenas se permitido
   */
  private safeVibrate(pattern: number | number[]): void {
    if (this.canUseVibration()) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        console.debug('Vibration blocked or failed:', error);
      }
    }
  }

  /**
   * Fornece feedback alternativo quando vibração não está disponível
   */
  private provideFallbackFeedback(priority: 'low' | 'normal' | 'high'): void {
    if (!this.canUseVibration() && priority === 'high') {
      // Para notificações de alta prioridade, pisca a tela como feedback alternativo
      if (document.body) {
        const originalOpacity = document.body.style.opacity;
        document.body.style.opacity = '0.8';
        setTimeout(() => {
          document.body.style.opacity = originalOpacity;
        }, 100);
      }
    }
  }
}
